import _merge from 'lodash/merge';
import {memoize} from '@packages/utilities/memo';
import type {
  AbilityDiagnosticsSelector,
  AbilityFailureReasons,
  AbilitySelector,
  AllAbilitiesSelector,
  ConfigDiagnostics,
  DesignerAbilityConfig,
  DesignerAbilityDefinition,
  DesignerAbilitySelectorName,
  ExtendedAbilitySelectorOptions,
  ExtendedDiagnosticAbilitySelectorOptions,
  SelectDesignerAbility,
  SingleAbilityDiagnosticsSelector,
  SelectDesignerAbilityDiagnostics,
  SingleAbilitySelector,
} from './types';
import {validators} from './sub-selectors';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {
  getPanel,
  getPanels,
  type ResolvedPanel,
  type PanelName,
} from '@packages/systems/panels';
import {entries} from '@packages/utilities/fp/utils';
import {values} from '@packages/utilities/key-map';
import {createSelector, createStructuredSelector} from 'reselect';
import {abilityConfigs} from '../abilities';

export const designerAbility =
  (...configs: Array<DesignerAbilityConfig>): AbilitySelector =>
  (state, _originalOptions) => {
    const originalOptions = _originalOptions as ExtendedAbilitySelectorOptions;
    // the recursiveSelectAbility is not exposed outside of the package,
    // but is used internally to allow us to short-circuit the ability
    // validation when generating snapshots.
    const options: ExtendedAbilitySelectorOptions = {
      ...originalOptions,
      recursiveSelectAbility:
        originalOptions?.recursiveSelectAbility ||
        (selectDesignerAbility as SelectDesignerAbility<DesignerAbilitySelectorName>),
    };

    // each ability definition can be a single config or an array of configs.
    // if there are multiple configs, we check if at least one is valid.
    return configs.some((config) => {
      // when checking an individual config, we need to ensure that every
      // requirement expressed in the config is met by the current state.
      const keys = Object.keys(config) as Array<keyof DesignerAbilityConfig>;
      return keys.every((validatorKey) => {
        const validator = validators[validatorKey];
        // the cast to any here is necessary because TS is not able to
        // narrow the type of the config object to the specific type
        // required by the validator, even though we know it's the
        // right type.
        const validatorConfig = config[validatorKey] as any;

        if (validatorConfig === undefined) return true;

        return validator.isValid(
          state as DesignerStores,
          validatorConfig,
          options
        );
      });
    });
  };

export const designerAbilityDiagnostics =
  (
    abilityName: DesignerAbilitySelectorName,
    ...configs: Array<DesignerAbilityConfig>
  ): AbilityDiagnosticsSelector =>
  (state, _originalOptions) => {
    const originalOptions =
      _originalOptions as ExtendedDiagnosticAbilitySelectorOptions;

    // the recursiveSelectAbility is not exposed outside of the package,
    // but is used internally to allow us to short-circuit the ability
    // validation when generating snapshots.
    const options: ExtendedDiagnosticAbilitySelectorOptions = {
      ...originalOptions,
      recursiveSelectAbility:
        originalOptions?.recursiveSelectAbility ||
        (selectDesignerAbility as SelectDesignerAbility<DesignerAbilitySelectorName>),
      recursiveSelectAbilityDiagnostics:
        originalOptions?.recursiveSelectAbilityDiagnostics ||
        (selectDesignerAbilityDiagnostics as SelectDesignerAbilityDiagnostics<DesignerAbilitySelectorName>),
    };

    // the designerAbilityDiagnostics selector returns a tuple of [isAbilityEnabled, diagnostics].
    //
    // isAbilityEnabled is a boolean, set to true if at least one of the configs is valid.
    // diagnostics is an object containing the reasons each config is invalid.
    let isAbilityEnabled = false;
    const diagnostics: AbilityFailureReasons = {};

    for (const config of configs) {
      // since we do not short-circuit the config checks, we need to
      // track the validity of the config separately from the
      // overall ability validity.
      let isConfigValid = true;

      // each config can have multiple reasons for being invalid, so we
      // keep track of them in a record.
      const configDiagnostics: ConfigDiagnostics = {};

      const keys = Object.keys(config) as Array<keyof DesignerAbilityConfig>;
      for (const validatorKey of keys) {
        const validator = validators[validatorKey];
        // the cast to any here is necessary because TS is not able to
        // narrow the type of the config object to the specific type
        // required by the validator, even though we know it's the
        // right type.
        const validatorConfig = config[validatorKey] as any;

        if (validatorConfig === undefined) continue;

        // if the validator has a diagnose function, use that to get
        // the detailed reasons for its validity.
        if (validator.diagnose) {
          const [isValid, reasons, dependencyReasons] = validator.diagnose(
            state as DesignerStores,
            validatorConfig,
            options
          );

          if (!isValid) {
            isConfigValid = false;
            // casting to `any` here, because at this point, TS doesn't know
            // that the `reasons` type is guaranteed to match the expected
            // type of `ConfigDiagnostics[validatorKey]`
            configDiagnostics[validatorKey] = reasons as any;
          }

          // the diagnose function can return diagnostics for other abilities
          // that the current ability depends upon, which we need to merge
          // into the top-level diagnostics.
          if (dependencyReasons) {
            _merge(diagnostics, dependencyReasons);
          }
        } else {
          // if the validator does not have a diagnose function, we can
          // only return a generic "failed validation" message.
          const isValid = validator.isValid(
            state as DesignerStores,
            validatorConfig,
            options
          );

          if (!isValid) {
            isConfigValid = false;
            configDiagnostics[validatorKey] = `failed validation`;
          }
        }
      }

      // add the config diagnostics to the top-level diagnostics
      if (!diagnostics[abilityName]) {
        diagnostics[abilityName] = [];
      }
      diagnostics[abilityName].push(configDiagnostics);

      // if the config is valid, we update the `isAbilityEnabled` to true, but
      // we do not short-circuit the loop, since we still want to check all
      // the configs and build up a complete set of diagnostics.
      if (isConfigValid) {
        isAbilityEnabled = true;
      }
    }

    return [isAbilityEnabled, diagnostics];
  };

type AbilitySelectors = Partial<
  Record<DesignerAbilitySelectorName, AbilitySelector>
>;
type DiagnosticSelectors = Partial<
  Record<DesignerAbilitySelectorName, AbilityDiagnosticsSelector>
>;
type AbilitiesAndDiagnostics = {
  abilitySelectors: AbilitySelectors;
  diagnosticsSelectors: DiagnosticSelectors;
  featureFlagsToStore: Set<string>;
};

function extractFeatureFlags(
  ...configs: Array<DesignerAbilityConfig>
): Array<string> {
  const flags: Array<string> = [];

  configs.forEach((config) => {
    if (config.featureFlags) {
      flags.push(...Object.keys(config.featureFlags));
    }
  });

  return flags;
}

const {abilitySelectors, diagnosticsSelectors, featureFlagsToStore} = entries<
  DesignerAbilityDefinition,
  DesignerAbilitySelectorName
>(abilityConfigs).reduce(
  (acc, [ability, config]) => {
    const isArray = Array.isArray(config);

    // abilities are re-calculated on every state update, and for performance
    // reasons, we want to avoid calling out to statsig on every update.
    //
    // statsig does not provide any way for us to grab "all the values", so
    // we need to figure out *which* flags the abilities care about, so we
    // can cache them up-front.
    //
    // this code extracts the feature flags from the ability configs, to
    // compile a list of all the flags which are used by any ability!
    (isArray
      ? extractFeatureFlags(...config)
      : extractFeatureFlags(config)
    ).forEach((flag) => {
      acc.featureFlagsToStore.add(flag);
    });

    const diagnosticsSelector = isArray
      ? designerAbilityDiagnostics(ability, ...config)
      : designerAbilityDiagnostics(ability, config);

    // we need to memoize the diagnostics selector manually. we could use
    // createStructuredSelector for this, but that would mean that any
    // time we call ONE of the diagnostics selectors, ALL of them
    // would get recomputed, even if their value is not used.
    //
    // by memoising this way, we can avoid re-computing the diagnostics
    // for abilities that ARE being queried (for example if multiple
    // abilities being diagnosed depend on one parent ability), but
    // avoid having to loop over abilities which aren't relevant.
    acc.diagnosticsSelectors[ability] = memoize(diagnosticsSelector);

    // the normal ability selectors do not need to be manually memoised, because
    // they ARE going to be passed through `createStructuredSelector`, since
    // it's both significantly less expensive to call them all, and also
    // significantly more likely that we'll be calling each ability.
    acc.abilitySelectors[ability] = isArray
      ? designerAbility(...config)
      : designerAbility(config);

    return acc;
  },
  {
    abilitySelectors: {},
    diagnosticsSelectors: {},
    featureFlagsToStore: new Set(),
  } as AbilitiesAndDiagnostics
);

export const featureFlags = featureFlagsToStore;

export const selectDesignerAbilities: AllAbilitiesSelector =
  createStructuredSelector(abilitySelectors as Record<any, AbilitySelector>);

export function selectDesignerAbility<
  Ability extends DesignerAbilitySelectorName,
>(ability: Ability): SingleAbilitySelector<Ability> {
  return abilitySelectors[ability] as unknown as SingleAbilitySelector<Ability>;
}

export function selectDesignerAbilityDiagnostics<
  Ability extends DesignerAbilitySelectorName,
>(ability: Ability): SingleAbilityDiagnosticsSelector<Ability> {
  return diagnosticsSelectors[
    ability
  ] as unknown as SingleAbilityDiagnosticsSelector<Ability>;
}

export const selectIsPanelEnabled = (
  state: DesignerStores,
  panelName: PanelName
) => {
  const panel = getPanel(state.PanelsStore, panelName);

  if (!panel) return false;

  const {abilityName} = panel.metadata;

  // @ts-expect-error - TS2722 - Cannot invoke an object which is possibly 'undefined'.
  return abilityName ? abilitySelectors[abilityName](state) : true;
};

type PanelsMap = Partial<Record<PanelName, ResolvedPanel<any>>>;
export const selectEnabledPanelsMap = createSelector(
  (state: DesignerStores) => getPanels(state.PanelsStore),
  selectDesignerAbilities,
  (panels, abilities) =>
    values(panels.all).reduce<PanelsMap>((acc, panel) => {
      if (!panel) return acc;
      const {abilityName} = panel.metadata;
      const isAbilityEnabled = abilityName ? abilities[abilityName] : true;
      if (isAbilityEnabled) {
        acc[panel.name] = panel;
      }
      return acc;
    }, {})
);
